import React, { useEffect, useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Users, FileCheck, FileX, Clock, DollarSign, Ban } from 'lucide-react';
import PortalApplicationsView from '../components/PortalApplicationsView';

const StatCard = ({ icon: Icon, label, value, bgColor, textColor }) => (
  <div className="stat-card bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-6 hover:shadow-md transition-shadow" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0 flex-1">
        <p className="text-xs sm:text-sm font-medium text-slate-500 uppercase tracking-wider truncate">{label}</p>
        <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1 sm:mt-2">{value}</p>
      </div>
      <div className={`p-3 sm:p-4 rounded-xl ${bgColor} flex-shrink-0`}>
        <Icon className={`w-6 h-6 sm:w-8 sm:h-8 ${textColor}`} />
      </div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/stats`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sidebar>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Admin Dashboard
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1 sm:mt-2">Overview of admission applications and payments</p>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading dashboard...</div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
              <StatCard
                icon={Users}
                label="Total Leads"
                value={stats?.total_leads || 0}
                bgColor="bg-blue-50"
                textColor="text-blue-600"
              />
              <StatCard
                icon={FileCheck}
                label="Successful"
                value={stats?.successful_leads || 0}
                bgColor="bg-green-50"
                textColor="text-green-600"
              />
              <StatCard
                icon={FileX}
                label="Unsuccessful"
                value={stats?.unsuccessful_leads || 0}
                bgColor="bg-red-50"
                textColor="text-red-600"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <StatCard
                icon={Clock}
                label="Pending Payments"
                value={stats?.pending_payments || 0}
                bgColor="bg-yellow-50"
                textColor="text-yellow-600"
              />
              <StatCard
                icon={DollarSign}
                label="Completed Payments"
                value={stats?.completed_payments || 0}
                bgColor="bg-green-50"
                textColor="text-green-600"
              />
              <StatCard
                icon={Ban}
                label="Ineligible Payments"
                value={stats?.ineligible_payments || 0}
                bgColor="bg-gray-50"
                textColor="text-gray-600"
              />
            </div>

            <div className="mt-8 bg-white border border-slate-200 rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <a
                  href="/colleges"
                  data-testid="quick-action-colleges"
                  className="block p-4 border-2 border-slate-200 rounded-md hover:border-[#0052CC] hover:bg-blue-50 transition-all text-center"
                >
                  <p className="font-medium text-slate-700">Manage Colleges</p>
                </a>
                <a
                  href="/agencies"
                  data-testid="quick-action-agencies"
                  className="block p-4 border-2 border-slate-200 rounded-md hover:border-[#0052CC] hover:bg-blue-50 transition-all text-center"
                >
                  <p className="font-medium text-slate-700">Manage Agencies</p>
                </a>
                <a
                  href="/applications"
                  data-testid="quick-action-applications"
                  className="block p-4 border-2 border-slate-200 rounded-md hover:border-[#0052CC] hover:bg-blue-50 transition-all text-center"
                >
                  <p className="font-medium text-slate-700">View Applications</p>
                </a>
                <a
                  href="/users"
                  data-testid="quick-action-users"
                  className="block p-4 border-2 border-slate-200 rounded-md hover:border-[#0052CC] hover:bg-blue-50 transition-all text-center"
                >
                  <p className="font-medium text-slate-700">User Management</p>
                </a>
              </div>
            </div>

            {/* Portal Applications View with Filters */}
            <div className="mt-8">
              <PortalApplicationsView />
            </div>
          </>
        )}
      </div>
    </Sidebar>
  );
}